import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {User} from '../models/users/user.model';
import {Users} from '../actions/users.actions';
import {UsersService} from '../services/users.service';
import {tap} from 'rxjs/operators';
import {Auth} from '../actions/auth.actions';
import ClearState = Auth.ClearState;
import GetUsers = Users.GetUsers;
import GetUser = Users.GetUser;
import CreateUser = Users.CreateUser;
import UpdateUser = Users.UpdateUser;
import DeleteUser = Users.DeleteUser;

export interface UsersStateModel {
  users: Array<User>;
  currentUser: User;
}

@State<UsersStateModel>({
  name: 'users',
  defaults: {
    users: [],
    currentUser: null,
  }
})
@Injectable()
export class UsersState {
  constructor(private usersService: UsersService
  ) {
  }

  @Selector()
  static users(state: UsersStateModel): Array<User> {
    return state.users;
  }

  @Selector()
  static user(state: UsersStateModel): User {
    return state.currentUser;
  }

  @Action(GetUsers)
  getUsers(ctx: StateContext<UsersStateModel>, action: GetUsers): Observable<User[]> {
    if (!action.force) {
      const users = ctx.getState().users;
      if (users.length > 0) {
        return of(users);
      }
    }
    return this.usersService.getUsers().pipe(
      tap(users => {
        ctx.patchState({
          users
        });
      })
    );
  }

  @Action(GetUser)
  getUser(ctx: StateContext<UsersStateModel>, action: GetUser): Observable<User> {
    if (!action.force) {
      const user = ctx.getState().currentUser;
      if (user && user.id === action.id) {
        return of(user);
      }

      const cachedUser = ctx.getState().users.find(u => u.id === action.id);
      if (cachedUser) {
        ctx.patchState({
          currentUser: cachedUser
        });
        return of(user);
      }
    }
    return this.usersService.getUser(action.id).pipe(
      tap(user => {
        ctx.patchState({
          currentUser: user
        });
      })
    );
  }

  @Action(CreateUser)
  createUser(ctx: StateContext<UsersStateModel>, action: CreateUser): Observable<User> {
    return this.usersService.createUser(action.user).pipe(
      tap(user => {
        ctx.patchState({
          currentUser: user
        });
      })
    );
  }

  @Action(UpdateUser)
  updateUser(ctx: StateContext<UsersStateModel>, action: UpdateUser): Observable<User> {
    return this.usersService.updateUser(action.user).pipe(
      tap(user => {
        ctx.patchState({
          currentUser: user
        });
      })
    );
  }

  @Action(DeleteUser)
  deleteUser(ctx: StateContext<UsersStateModel>, action: DeleteUser): Observable<User> {
    const users = ctx.getState().users;
    const index = users.indexOf(action.user);
    return this.usersService.deleteUser(action.user).pipe(
      tap(user => {
        ctx.patchState({
          currentUser: null,
          users: [
            ...users.slice(0, index),
            ...index === users.length - 1 ? [] : users.slice(index + 1)
          ]
        });
      })
    );
  }

  @Action(ClearState)
  clearState(ctx: StateContext<UsersStateModel>): void {
    ctx.setState({
      users: [],
      currentUser: null,
    });
  }
}
